import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { Calendar, Tag as TagIcon, Folder, ArrowRight, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return ''; }
};

const PostCard = ({ post, featured = false }) => (
  <Link
    to={`/blog/${post.slug}`}
    data-testid={`blog-card-${post.slug}`}
    className={`group block bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 ${featured ? 'ring-4 ring-[#E91E8C] ring-opacity-40' : ''}`}
  >
    {post.hero_image_url ? (
      <div className="aspect-[16/9] overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
        <OptimizedImage
          src={post.hero_image_url}
          alt={post.hero_image_alt || post.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
    ) : (
      <div className="aspect-[16/9] bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] flex items-center justify-center">
        <span className="text-6xl">🍦</span>
      </div>
    )}
    <div className="p-6">
      {post.categories?.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {post.categories.slice(0, 2).map((cat) => (
            <span key={cat} className="inline-block bg-pink-100 text-[#E91E8C] text-xs font-bold px-3 py-1 rounded-full">
              {cat}
            </span>
          ))}
        </div>
      )}
      <h3
        className={`font-black text-gray-900 group-hover:text-[#E91E8C] transition-colors mb-2 ${featured ? 'text-2xl md:text-3xl' : 'text-xl'}`}
        style={{ fontFamily: 'Rowdies, cursive' }}
      >
        {post.title}
      </h3>
      {post.excerpt && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
      )}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(post.publish_date)}
        </span>
        <span className="flex items-center gap-1 text-[#E91E8C] font-bold group-hover:translate-x-1 transition-transform">
          Read <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  </Link>
);

const NewsletterBox = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/blog/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'blog' }),
      });
      if (res.ok) {
        setStatus({ ok: true, msg: "Thanks! You're subscribed — sweet stories coming your way." });
        setEmail('');
      } else {
        setStatus({ ok: false, msg: 'Something went wrong. Try again?' });
      }
    } catch {
      setStatus({ ok: false, msg: 'Network error. Try again?' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] text-white rounded-3xl p-6 shadow-xl">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-5 h-5" />
        <h3 className="text-xl font-black" style={{ fontFamily: 'Rowdies, cursive' }}>Scoop Club</h3>
      </div>
      <p className="text-white/90 text-sm mb-4">Get new posts + flavor drops in your inbox.</p>
      <form onSubmit={submit} className="space-y-2" data-testid="newsletter-form">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="newsletter-email-input"
          className="w-full px-4 py-3 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-white/30"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit-btn"
          className="w-full bg-white text-[#E91E8C] font-bold py-3 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-50"
        >
          {loading ? 'Subscribing…' : 'Subscribe'}
        </button>
      </form>
      {status && (
        <p className={`text-sm mt-3 ${status.ok ? 'text-white' : 'text-yellow-100'}`}>{status.msg}</p>
      )}
    </div>
  );
};

const Blog = () => {
  const { category, tag } = useParams();
  const [posts, setPosts] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterLabel = category ? `Category: ${category}` : tag ? `Tag: ${tag}` : null;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams({ limit: '60' });
        if (category) qs.set('category', category);
        if (tag) qs.set('tag', tag);

        const [postsRes, featRes, catsRes, tagsRes] = await Promise.all([
          fetch(`${API}/api/blog/posts?${qs.toString()}`),
          fetch(`${API}/api/blog/posts?featured=true&limit=1`),
          fetch(`${API}/api/blog/categories`),
          fetch(`${API}/api/blog/tags`),
        ]);
        const pd = postsRes.ok ? await postsRes.json() : { posts: [] };
        const fd = featRes.ok ? await featRes.json() : { posts: [] };
        const cd = catsRes.ok ? await catsRes.json() : { categories: [] };
        const td = tagsRes.ok ? await tagsRes.json() : { tags: [] };
        setPosts(pd.posts || []);
        // Only show featured on the main /blog view (not filtered)
        setFeatured(!category && !tag ? (fd.posts?.[0] || null) : null);
        setCategories(cd.categories || []);
        setTags(td.tags || []);
      } catch (e) {
        console.error('Blog load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [category, tag]);

  const nonFeaturedPosts = featured ? posts.filter((p) => p.slug !== featured.slug) : posts;

  return (
    <div
      className="min-h-screen pt-32"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed',
      }}
    >
      <SEO
        title={filterLabel ? `Blog — ${filterLabel}` : 'Blog & News'}
        description="Stories, recipes, flavor drops, and ice-cream & pastry industry insights from 1st Ave ICP in Mineral Wells, TX."
        keywords="ice cream blog, pastry blog, mineral wells ice cream, ice cream industry, ice cream recipes, flavor drops"
        canonical={category ? `/blog/category/${category}` : tag ? `/blog/tag/${tag}` : '/blog'}
        type="website"
        noindex={Boolean(category || tag)}
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#E91E8C] to-[#9B4DCA] pt-16 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h1 className="text-4xl md:text-6xl font-black mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>
            {filterLabel ? filterLabel : 'The Scoop'}
          </h1>
          <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
            Stories, flavor drops, and behind-the-scenes from the 1st Ave ICP kitchen.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-16" viewBox="0 0 1440 64" preserveAspectRatio="none">
            <path fill="#ffffff" d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,48 1440,32 L1440,64 L0,64 Z" />
          </svg>
        </div>
      </section>

      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-3 gap-10">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-10">
            {loading ? (
              <div className="text-center py-16 text-xl font-black text-gray-400">Loading…</div>
            ) : (
              <>
                {featured && (
                  <div>
                    <div className="mb-4">
                      <span className="inline-block bg-[#E91E8C] text-white px-4 py-1 rounded-full text-xs font-black">
                        ★ FEATURED
                      </span>
                    </div>
                    <PostCard post={featured} featured />
                  </div>
                )}

                {nonFeaturedPosts.length === 0 && !featured ? (
                  <div className="text-center py-16">
                    <p className="text-xl text-gray-500">No posts yet. Check back soon!</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-6">
                    {nonFeaturedPosts.map((p) => (
                      <PostCard key={p.id || p.slug} post={p} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-8">
            <NewsletterBox />

            {categories.length > 0 && (
              <div className="bg-gray-50 rounded-3xl p-6">
                <h3 className="flex items-center gap-2 text-lg font-black mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>
                  <Folder className="w-5 h-5 text-[#E91E8C]" /> Categories
                </h3>
                <ul className="space-y-2">
                  {categories.map((c) => (
                    <li key={c.name}>
                      <Link
                        to={`/blog/category/${encodeURIComponent(c.name)}`}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg hover:bg-pink-100 hover:text-[#E91E8C] transition-colors ${category === c.name ? 'bg-pink-100 text-[#E91E8C] font-bold' : 'text-gray-700'}`}
                      >
                        <span>{c.name}</span>
                        <span className="text-xs bg-white px-2 py-0.5 rounded-full">{c.count}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tags.length > 0 && (
              <div className="bg-gray-50 rounded-3xl p-6">
                <h3 className="flex items-center gap-2 text-lg font-black mb-4" style={{ fontFamily: 'Rowdies, cursive' }}>
                  <TagIcon className="w-5 h-5 text-[#E91E8C]" /> Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map((t) => (
                    <Link
                      key={t.name}
                      to={`/blog/tag/${encodeURIComponent(t.name)}`}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${tag === t.name ? 'bg-[#E91E8C] text-white border-[#E91E8C]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#E91E8C] hover:text-[#E91E8C]'}`}
                    >
                      {t.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Blog;
